/**
 * 거래 일괄 입력(A-7) 검증 순수 함수
 *
 * @why 화면(테이블/카드)이 둘로 갈리므로 규칙을 화면에서 분리해 한 곳에 모은다.
 * 순수 함수라 표현이 무엇이든 동일하게 동작하고, DOM 없이 단위 테스트할 수 있다.
 */

import type { Category } from '@/domains/category/types/Category'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { User } from '@/domains/user/types/User'
import type {
  BulkRowError,
  BulkRowValidation,
  BulkTransactionDraftRow,
} from '../types/BulkTransaction'

/**
 * 검증에 필요한 마스터 데이터
 *
 * @why 카테고리/결제수단/멤버는 다른 사용자가 언제든 바꿀 수 있다.
 * 검증 함수가 이 값을 인자로 받게 해서, 저장 직전에 "최신 마스터"로 다시 검증하는
 * 동시 편집 방어를 같은 함수로 수행할 수 있게 한다.
 */
export interface BulkValidationContext {
  /** 대분류 목록 (각 대분류의 children에 소분류) */
  categories: Category[]
  paymentMethods: PaymentMethod[]
  users: User[]
}

/**
 * 전체 검증 결과
 */
export interface BulkValidationSummary {
  /** clientId → 행 검증 결과 */
  byClientId: Record<string, BulkRowValidation>
  /** 저장 대상(빈 행 제외 + 오류 없음) 건수 */
  validCount: number
  /** 오류가 있는 행 수 */
  errorCount: number
  /** 저장 버튼 활성화 여부 */
  canSave: boolean
}

/**
 * 금액 문자열을 정수로 파싱한다.
 *
 * @why `parseFloat`을 그대로 쓰면 "1,000"이 1로 조용히 잘리고 "abc"가 NaN이 되어
 * 시트에 잘못된 값이 영구 기록된다. 천 단위 구분자는 받아주되,
 * 그 외에는 명시적으로 거부한다.
 *
 * 소수를 거부하는 이유: 데이터 모델의 amount는 정수이고 원화 가계부라 소수 금액이
 * 의미를 갖지 않는다. (단건 폼이 parseFloat을 쓰던 읽기/쓰기 비대칭을 여기서 바로잡음)
 *
 * @returns 유효한 양의 정수면 그 값, 아니면 null
 */
function parseAmount(raw: string): number | null {
  const normalized = raw.replace(/[,\s]/g, '')

  if (normalized === '') return null

  const value = Number(normalized)

  if (!Number.isFinite(value)) return null
  if (!Number.isInteger(value)) return null
  if (value <= 0) return null

  return value
}

/**
 * 행 1개를 검증한다.
 *
 * @param row 검증할 Draft 행
 * @param ctx 참조 매칭에 쓸 마스터 데이터
 */
export function validateRow(
  row: BulkTransactionDraftRow,
  ctx: BulkValidationContext
): BulkRowValidation {
  const amount = parseAmount(row.amount)

  /**
   * 빈 행 판정
   *
   * @why 행을 추가하면 직전 행의 날짜/구분/결제수단/사람이 자동 복사된다.
   * 이 복사된 값들을 "입력됨"으로 세면 사용자가 아직 손대지 않은 마지막 행이
   * 늘 "일부만 입력된 오류 행"이 되고, 오류가 있으면 저장을 막는 정책과 맞물려
   * 저장이 영구히 불가능해진다.
   * 그래서 자동 복사되지 않는 필드(금액/대분류/메모) 즉 "사용자가 직접 넣어야만
   * 생기는 값"이 하나도 없을 때만 빈 행으로 본다.
   */
  const isEmpty =
    row.amount.trim() === '' &&
    row.majorCategoryId === '' &&
    row.memo.trim() === ''

  if (isEmpty) {
    return { clientId: row.clientId, isEmpty: true, errors: [], amount: null }
  }

  const errors: BulkRowError[] = []
  const isIncome = row.type === 'income'

  // 날짜
  if (!row.date) {
    errors.push({ field: 'date', message: '날짜를 입력해주세요' })
  }

  // 금액
  if (row.amount.trim() === '') {
    errors.push({ field: 'amount', message: '금액을 입력해주세요' })
  } else if (amount === null) {
    errors.push({ field: 'amount', message: '금액은 0보다 큰 정수여야 합니다' })
  }

  // 대분류 (+ 거래 구분과의 일치, 참조 매칭)
  const major = ctx.categories.find((c) => c.id === row.majorCategoryId)

  if (!row.majorCategoryId) {
    errors.push({ field: 'majorCategoryId', message: '대분류를 선택해주세요' })
  } else if (!major || !major.isActive) {
    errors.push({ field: 'majorCategoryId', message: '존재하지 않는 카테고리입니다' })
  } else if (major.type !== row.type) {
    errors.push({
      field: 'majorCategoryId',
      message: `${isIncome ? '수입' : '지출'} 카테고리를 선택해주세요`,
    })
  }

  // 소분류 (선택 사항이지만 넣었다면 선택한 대분류의 하위여야 함)
  if (row.minorCategoryId) {
    const minor = major?.children?.find((c) => c.id === row.minorCategoryId)

    if (!minor) {
      errors.push({
        field: 'minorCategoryId',
        message: '선택한 대분류의 소분류가 아닙니다',
      })
    }
  }

  /**
   * 결제수단 — 지출만 검증한다.
   *
   * @why 수입은 "입금"이라 결제수단 개념이 없어 화면에 칸 자체를 띄우지 않는다.
   * 지출→수입으로 바꾸면서 값이 남아 있을 수 있는데, 이를 오류로 만들면
   * 사용자가 화면에서 고칠 방법이 없는 오류가 되어 저장이 막힌다.
   * 그래서 수입의 결제수단은 오류 대신 저장 시 빈 값으로 무시한다.
   */
  if (!isIncome) {
    const paymentMethod = ctx.paymentMethods.find(
      (pm) => pm.id === row.paymentMethodId
    )

    if (!row.paymentMethodId) {
      errors.push({ field: 'paymentMethodId', message: '결제수단을 선택해주세요' })
    } else if (!paymentMethod || !paymentMethod.isActive) {
      errors.push({
        field: 'paymentMethodId',
        message: '존재하지 않는 결제수단입니다',
      })
    }
  }

  // 사람 (지출=결제 계좌 소유자 / 수입=받은 사람)
  const spenderLabel = isIncome ? '받은 사람' : '결제 계좌 소유자'
  const spender = ctx.users.find((u) => u.id === row.spenderId)

  if (!row.spenderId) {
    errors.push({ field: 'spenderId', message: `${spenderLabel}을(를) 선택해주세요` })
  } else if (!spender || !spender.isActive) {
    errors.push({ field: 'spenderId', message: '존재하지 않는 멤버입니다' })
  }

  return { clientId: row.clientId, isEmpty: false, errors, amount }
}

/**
 * 전체 행을 검증하고 저장 가능 여부를 판정한다.
 *
 * @why 저장은 단일 append 요청 한 번으로 이뤄져 통째로 성공하거나 통째로 실패한다.
 * 부분 저장이라는 중간 상태가 없으므로, 오류가 하나라도 있으면 저장 자체를 막아
 * "일부만 들어간 애매한 상태"를 원천적으로 만들지 않는다.
 */
export function validateAll(
  rows: BulkTransactionDraftRow[],
  ctx: BulkValidationContext
): BulkValidationSummary {
  const byClientId: Record<string, BulkRowValidation> = {}
  let validCount = 0
  let errorCount = 0

  for (const row of rows) {
    const result = validateRow(row, ctx)
    byClientId[row.clientId] = result

    if (result.isEmpty) continue

    if (result.errors.length > 0) {
      errorCount += 1
    } else {
      validCount += 1
    }
  }

  return {
    byClientId,
    validCount,
    errorCount,
    canSave: errorCount === 0 && validCount > 0,
  }
}
